import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

function inputClass() {
  return "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100";
}

function mapPasswordError(err) {
  const msg = (err?.message ?? "").toLowerCase();
  if (msg.includes("same password") || msg.includes("different from the old"))
    return "Nowe hasło musi być inne niż obecne.";
  if (msg.includes("weak") || msg.includes("too short") || msg.includes("at least"))
    return "Hasło jest za słabe. Użyj co najmniej 6 znaków.";
  if (msg.includes("session") || msg.includes("not authenticated"))
    return "Sesja wygasła. Zaloguj się ponownie.";
  return "Nie udało się zmienić hasła. Spróbuj ponownie.";
}

export default function ChangePasswordPanel({ delay = 0 }) {
  const { changePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (newPassword.length < 6) {
      setError("Hasło musi mieć co najmniej 6 znaków.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Hasła nie są identyczne. Sprawdź i spróbuj ponownie.");
      return;
    }

    setSaving(true);
    try {
      await changePassword(newPassword);
      setSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Hasło zostało zmienione.");
    } catch (err) {
      setError(mapPasswordError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)] backdrop-blur-sm"
    >
      {/* Nagłówek */}
      <div className="mb-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700/80">
          Bezpieczeństwo
        </div>
        <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">
          Zmień hasło
        </div>
        <div className="mt-1 text-sm text-slate-500">
          Ustaw nowe hasło do logowania w aplikacji.
        </div>
      </div>

      {/* Info dla użytkowników magic link */}
      <div className="mb-5 rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3">
        <p className="text-xs leading-5 text-sky-700">
          Jeśli dotychczas logowałeś(-aś) się linkiem e-mail, ustawienie hasła umożliwi
          Ci logowanie również przez zakładkę <strong>„Zaloguj się hasłem"</strong>.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            Nowe hasło
          </label>
          <input
            type="password"
            className={inputClass()}
            placeholder="min. 6 znaków"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            Potwierdź nowe hasło
          </label>
          <input
            type="password"
            className={inputClass()}
            placeholder="powtórz hasło"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700"
          >
            Hasło zostało pomyślnie zmienione.
          </motion.div>
        )}

        <button
          type="submit"
          disabled={saving}
          className={`flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-all duration-200 ${
            saving
              ? "cursor-not-allowed opacity-60"
              : "hover:-translate-y-0.5"
          }`}
        >
          {saving ? (
            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : null}
          {saving ? "Zapisywanie..." : "Zmień hasło"}
        </button>
      </form>
    </motion.div>
  );
}
